import React, { useEffect, useState } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import axios from 'axios';
import { Check, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import LeadCapturePopup from '../components/LeadCapturePopup';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ServiceDetail = () => {
  const { serviceId } = useParams();
  const [isVisible, setIsVisible] = useState(false);
  const [service, setService] = useState(null);
  const [detailContent, setDetailContent] = useState(null);
  const [otherServices, setOtherServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showLeadPopup, setShowLeadPopup] = useState(false);
  const [inventoryRedirectUrl, setInventoryRedirectUrl] = useState('');

  const fetchServiceData = async () => {
    try {
      const [serviceRes, detailRes, allServicesRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/cms/services`),
        axios.get(`${BACKEND_URL}/api/cms/service-details/${serviceId}`),
        axios.get(`${BACKEND_URL}/api/cms/services`)
      ]);

      const foundService = serviceRes.data.find(s => s.service_id === parseInt(serviceId));
      setService(foundService);
      setDetailContent(detailRes.data.content);
      setOtherServices(allServicesRes.data.filter(s => s.service_id !== parseInt(serviceId)).slice(0, 2));
      setLoading(false);
    } catch (error) {
      console.error('Error fetching service data:', error);
      setLoading(false);
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    setTimeout(() => setIsVisible(true), 100);
    fetchServiceData();
  }, [serviceId]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-2xl text-[#B5BBC4]">Loading...</div>
      </div>
    );
  }

  // If service not found, redirect to homepage
  if (!service) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section with Service Image */}
      <section className="relative h-[70vh] overflow-hidden bg-[#0A1A50]">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${detailContent?.hero_image || service.image})` }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/70"></div>
        </div>
        
        <div className="relative h-full flex items-center justify-center text-center px-6">
          <div 
            className="max-w-4xl"
            style={{
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
              transition: 'opacity 0.8s ease-out, transform 0.8s ease-out'
            }}
          >
            <h1 className="text-6xl md:text-7xl font-light text-white mb-6">
              {detailContent?.hero_title || service.title}
            </h1>
            <p className="text-white/90 text-xl md:text-2xl font-light">
              {detailContent?.hero_subtitle || service.description}
            </p>
          </div>
        </div>
      </section>

      {/* Service Details */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Description */}
            <div
              style={{
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? 'translateX(0)' : 'translateX(-30px)',
                transition: 'opacity 0.8s ease-out 0.3s, transform 0.8s ease-out 0.3s'
              }}
            >
              <div className="h-1 w-20 bg-[#0A1A50] mb-6"></div>
              <h2 className="text-4xl font-light text-[#2d2d2d] mb-6">
                {detailContent?.overview_title || 'Overview'}
              </h2>
              <p className="text-[#B5BBC4] text-lg leading-relaxed font-light mb-8 whitespace-pre-line">
                {detailContent?.overview_content || service.description}
              </p>
            </div>

            {/* Benefits */}
            <div
              style={{
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? 'translateX(0)' : 'translateX(30px)',
                transition: 'opacity 0.8s ease-out 0.5s, transform 0.8s ease-out 0.5s'
              }}
            >
              <h2 className="text-4xl font-light text-[#2d2d2d] mb-8">
                {detailContent?.benefits_title || 'Key Benefits'}
              </h2>
              <div className="space-y-4">
                {(detailContent?.benefits || []).map((benefit, idx) => (
                  <div 
                    key={idx} 
                    className="flex items-start gap-4 p-4 rounded-lg hover:bg-gray-50 transition-all duration-300 group"
                  >
                    <div className="w-10 h-10 rounded-full bg-[#0A1A50]/20 flex items-center justify-center flex-shrink-0 group-hover:bg-[#0A1A50] group-hover:scale-110 transition-all duration-300">
                      <span className="text-2xl">{benefit.icon || '✓'}</span>
                    </div>
                    <div>
                      <h4 className="text-xl font-light text-[#2d2d2d] mb-1">{benefit.title}</h4>
                      <p className="text-[#B5BBC4] font-light text-sm">{benefit.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      {detailContent?.process_steps && detailContent.process_steps.length > 0 && (
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6">
            <h2 className="text-4xl font-light text-[#2d2d2d] mb-12 text-center">
              {detailContent?.process_title || 'Our Process'}
            </h2>
            <div className={`flex flex-wrap justify-center gap-8 ${detailContent.process_steps.length >= 4 ? 'max-w-7xl' : 'max-w-4xl'} mx-auto`}>
              {detailContent.process_steps.map((item, idx) => (
                <div 
                  key={idx}
                  className={`text-center group hover:-translate-y-2 transition-transform duration-300 ${
                    detailContent.process_steps.length >= 4 ? 'w-full md:w-[calc(50%-1rem)] lg:w-[calc(25%-1.5rem)]' : 'w-full md:w-[calc(50%-1rem)] lg:w-[280px]'
                  }`}
                >
                  <div className="text-6xl font-light text-[#0A1A50]/20 group-hover:text-[#0A1A50] transition-colors mb-4 text-center">
                    {item.step || `0${idx + 1}`}
                  </div>
                  <h3 className="text-xl font-light text-[#2d2d2d] mb-2 text-center">{item.title}</h3>
                  <p className="text-[#B5BBC4] font-light text-sm text-center">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Special Section - For Investment service (service_id 3) */}
      {service?.service_id === 3 && detailContent?.special_section_title && (
        <section className="py-20 bg-[#0A1A50]">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-4xl md:text-5xl font-light text-white mb-8">
              {detailContent.special_section_title}
            </h2>
            {detailContent.special_section_button_text && (
              <Button 
                onClick={() => {
                  setInventoryRedirectUrl(detailContent.special_section_button_link || '#');
                  setShowLeadPopup(true);
                }}
                className="bg-white text-[#0A1A50] hover:bg-white/90 px-8 py-6 text-lg rounded-full shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-105 group"
                data-testid="inventory-access-btn"
              >
                {detailContent.special_section_button_text}
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
            )}
          </div>
        </section>
      )}

      {/* Lead Capture Popup for Inventory Access */}
      <LeadCapturePopup
        isOpen={showLeadPopup}
        onClose={() => setShowLeadPopup(false)}
        redirectUrl={inventoryRedirectUrl}
        title="Access Our Land Inventory"
        subtitle="Please provide your details to access our exclusive land inventory database."
      />

      {/* Other Services */}
      {otherServices.length > 0 && (
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <h2 className="text-4xl font-light text-[#2d2d2d] mb-12 text-center">Explore Other Services</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {otherServices.map((otherService) => (
                <Link
                  key={otherService.service_id}
                  to={`/services/${otherService.service_id}`}
                  className="group relative h-80 overflow-hidden rounded-lg shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2"
                >
                  <img
                    src={otherService.image}
                    alt={otherService.title}
                    className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent">
                    <div className="absolute bottom-0 left-0 right-0 p-8">
                      <h3 className="text-3xl font-light text-white mb-2 group-hover:translate-x-2 transition-transform duration-300">
                        {otherService.title}
                      </h3>
                      <p className="text-white/80 font-light mb-4">{otherService.description}</p>
                      <div className="flex items-center text-[#0A1A50] font-light">
                        Learn More <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-2 transition-transform" />
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-20 bg-[#0A1A50]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl font-light text-white mb-6">
            {detailContent?.cta_title || `Ready to get started with ${service.title}?`}
          </h2>
          <p className="text-white/90 text-lg font-light mb-8">
            {detailContent?.cta_subtitle || "Let's discuss how we can help achieve your goals"}
          </p>
          <Link to="/contact">
            <Button className="bg-white text-[#0A1A50] hover:bg-white/90 px-8 py-6 text-lg rounded-full shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-105 group">
              Contact Us
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ServiceDetail;